import type { RecoveryAttachmentCounts, RecoveryAttached } from '@/lib/api/schemas/product-recovery.schema'

/**
 * What moves if the decision is made.
 *
 * A person deciding about money sees what moves before they move it. Essentially
 * all of the money still hangs on legacy rows, so a confirm button that appears
 * before this block is the failure this surface exists to end.
 *
 * A count of nothing is stated in words. A zero in a cell reads like a fact that
 * failed to load, and this screen never asks the owner to tell those apart.
 */
function StakeGroup({
  name,
  label,
  count,
  shown,
  children,
}: {
  name: string
  label: string
  count: number
  shown: number
  children: React.ReactNode
}) {
  return (
    <section className="rounded-md border border-rule bg-paper p-2" data-stake={name} data-stake-count={count}>
      <h3 className="text-xs font-medium text-ink">
        {label}: {count === 0 ? 'ничего не привязано' : count.toLocaleString('ru-RU')}
      </h3>
      {count === 0 ? null : (
        <>
          <ul className="mt-1 space-y-0.5 text-[11px] text-ink-2">{children}</ul>
          {shown < count ? (
            <p className="mt-1 text-[11px] text-ink-muted" data-stake-truncated={count - shown}>
              Показаны {shown.toLocaleString('ru-RU')} из {count.toLocaleString('ru-RU')}.
            </p>
          ) : null}
        </>
      )}
    </section>
  )
}

export function SubjectStake({
  counts,
  attached,
}: {
  counts: RecoveryAttachmentCounts
  attached: RecoveryAttached
}) {
  return (
    <div className="grid gap-2 sm:grid-cols-2" data-testid="recovery-stake">
      <StakeGroup name="orders" label="Заказы" count={counts.orderLines} shown={attached.orders.length}>
        {attached.orders.map((order) => (
          <li key={order.orderId} data-order={order.orderId}>
            <span className="font-mono">{order.externalOrderId ?? order.orderId}</span>
            {' · '}{order.providerCode}
            {order.placedAt === null ? null : <> · {new Date(order.placedAt).toLocaleDateString('ru-RU')}</>}
          </li>
        ))}
      </StakeGroup>

      <StakeGroup name="sales" label="Продажи" count={counts.salesLinks} shown={attached.salesLinks.length}>
        {attached.salesLinks.map((sale) => (
          <li key={sale.salesLinkId} data-sale={sale.salesLinkId}>
            <span className="font-mono">{sale.orderId ?? sale.salesLinkId}</span>
            {' · '}{sale.providerCode}
            {sale.matchRule === null ? null : <> · {sale.matchRule}</>}
          </li>
        ))}
      </StakeGroup>

      <StakeGroup name="listings" label="Объявления" count={counts.listings} shown={attached.listings.length}>
        {attached.listings.map((listing) => (
          <li key={listing.listingId} data-listing={listing.listingId}>
            <span className="font-mono">{listing.externalOfferId ?? listing.listingId}</span>
            {' · '}{listing.accountCode}
          </li>
        ))}
      </StakeGroup>

      <StakeGroup name="media" label="Фотографии" count={counts.media} shown={attached.media.length}>
        {attached.media.map((media) => (
          <li key={media.mediaId} data-media={media.mediaId}>
            <span className="font-mono">{media.mediaId}</span>
            {' · '}{media.kind}
            {media.isPrimary ? ' · основная' : ''}
          </li>
        ))}
      </StakeGroup>
    </div>
  )
}
